import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

interface AlertItem {
  id: string;
  severity: string;
  category: string;
  title: string;
  description: string;
  equipment_id: string;
  equipment_tag: string;
  component_id: string | null;
  component_desc: string | null;
  due_date: string | null;
  days_remaining: number | null;
  organisation_id: string;
}

interface AlertSummary {
  total: number;
  critical: number;
  warning: number;
  info: number;
  alerts: AlertItem[];
}

const SEV_CONFIG: Record<string, { icon: string; bg: string; border: string; text: string; label: string }> = {
  critical: { icon: '🔴', bg: '#ffebee', border: '#f44336', text: '#c62828', label: 'Critical' },
  warning: { icon: '🟡', bg: '#fff3e0', border: '#ff9800', text: '#e65100', label: 'Warning' },
  info: { icon: '🔵', bg: '#e3f2fd', border: '#2196f3', text: '#1565c0', label: 'Info' },
};

const CAT_LABELS: Record<string, { icon: string; label: string }> = {
  overhaul_overdue: { icon: '🔧', label: 'Overhaul Overdue' },
  overhaul_approaching: { icon: '⏰', label: 'Overhaul Approaching' },
  mda_deadline: { icon: '⚠️', label: 'MDA Deadline' },
  psf_incomplete: { icon: '📋', label: 'PSF Incomplete' },
  cert_expiry: { icon: '📜', label: 'Certificate Expiry' },
};

const Alerts: React.FC = () => {
  const [data, setData] = useState<AlertSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [catFilter, setCatFilter] = useState('');
  const [sevFilter, setSevFilter] = useState('');
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('psf_user') || '{}');

  const loadAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (catFilter) params.category = catFilter;
      if (sevFilter) params.severity = sevFilter;
      const { data } = await api.get<AlertSummary>('/api/alerts/', { params });
      setData(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [catFilter, sevFilter]);

  useEffect(() => { loadAlerts(); }, [loadAlerts]);

  const handleLogout = () => {
    localStorage.removeItem('psf_token');
    localStorage.removeItem('psf_user');
    navigate('/login');
  };

  return (
    <div style={s.container}>
      <header style={s.header}>
        <div style={s.headerLeft}>
          <button onClick={() => navigate('/')} style={s.backBtn}>← Dashboard</button>
          <img src="/logo-white.png" alt="JMB Engineering" style={{ height: '36px' }} />
          <div style={{ width: '1px', height: '32px', background: 'rgba(255,255,255,0.2)' }} />
          <div>
            <h1 style={s.headerTitle}>PSF Platform</h1>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '1px', letterSpacing: '0.5px' }}>Plant Safety File Platform</div>
          </div>
          <span style={s.pageBadge}>Alerts</span>
        </div>
        <div style={s.headerRight}>
          <span style={s.userName}>{user.name}</span>
          <button onClick={handleLogout} style={s.backBtn}>Logout</button>
        </div>
      </header>

      {/* Summary Cards */}
      {data && (
        <div style={s.summaryRow}>
          <div style={{ ...s.summaryCard, borderLeft: '4px solid #f44336' }} onClick={() => setSevFilter(sevFilter === 'critical' ? '' : 'critical')}>
            <div style={{ fontSize: '28px', fontWeight: 700, color: '#c62828' }}>{data.critical}</div>
            <div style={s.summaryLabel}>Critical</div>
          </div>
          <div style={{ ...s.summaryCard, borderLeft: '4px solid #ff9800' }} onClick={() => setSevFilter(sevFilter === 'warning' ? '' : 'warning')}>
            <div style={{ fontSize: '28px', fontWeight: 700, color: '#e65100' }}>{data.warning}</div>
            <div style={s.summaryLabel}>Warnings</div>
          </div>
          <div style={{ ...s.summaryCard, borderLeft: '4px solid #2196f3' }} onClick={() => setSevFilter(sevFilter === 'info' ? '' : 'info')}>
            <div style={{ fontSize: '28px', fontWeight: 700, color: '#1565c0' }}>{data.info}</div>
            <div style={s.summaryLabel}>Info</div>
          </div>
          <div style={{ ...s.summaryCard, borderLeft: '4px solid #4caf50' }}>
            <div style={{ fontSize: '28px', fontWeight: 700, color: '#2e7d32' }}>{data.total}</div>
            <div style={s.summaryLabel}>Total Alerts</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={s.filters}>
        <select value={catFilter} onChange={e => setCatFilter(e.target.value)} style={s.select}>
          <option value="">All Categories</option>
          <option value="overhaul_overdue">🔧 Overhaul Overdue</option>
          <option value="overhaul_approaching">⏰ Overhaul Approaching</option>
          <option value="mda_deadline">⚠️ MDA Deadline</option>
          <option value="psf_incomplete">📋 PSF Incomplete</option>
          <option value="cert_expiry">📜 Certificate Expiry</option>
        </select>
        <select value={sevFilter} onChange={e => setSevFilter(e.target.value)} style={s.select}>
          <option value="">All Severities</option>
          <option value="critical">🔴 Critical</option>
          <option value="warning">🟡 Warning</option>
          <option value="info">🔵 Info</option>
        </select>
        {(catFilter || sevFilter) && (
          <button onClick={() => { setCatFilter(''); setSevFilter(''); }} style={s.clearBtn}>
            ✕ Clear Filters
          </button>
        )}
      </div>

      {/* Alert List */}
      <div style={s.alertList} id="alert-list-wrap">
        {loading ? (
          <div style={s.loadingText}>Scanning equipment...</div>
        ) : !data || data.alerts.length === 0 ? (
          <div style={s.emptyState}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
            <h3 style={{ margin: '0 0 8px', color: '#333' }}>All clear!</h3>
            <p style={{ color: '#888', margin: 0 }}>No compliance alerts to show.</p>
          </div>
        ) : (
          data.alerts.map(alert => {
            const sev = SEV_CONFIG[alert.severity] || SEV_CONFIG.info;
            const cat = CAT_LABELS[alert.category] || { icon: '📌', label: alert.category };
            return (
              <div
                key={alert.id}
                style={{ ...s.alertCard, borderLeftColor: sev.border, background: sev.bg }}
                onClick={() => navigate(`/equipment/${alert.equipment_id}`)}
              >
                <div style={s.alertTop}>
                  <div style={s.alertMeta}>
                    <span style={{ ...s.sevBadge, background: sev.border }}>{sev.label}</span>
                    <span style={s.catBadge}>{cat.icon} {cat.label}</span>
                  </div>
                  {alert.days_remaining !== null && (
                    <div style={{
                      ...s.daysChip,
                      background: alert.days_remaining < 0 ? '#f44336' : alert.days_remaining < 90 ? '#ff9800' : '#4caf50',
                    }}>
                      {alert.days_remaining < 0
                        ? `${Math.abs(alert.days_remaining)}d overdue`
                        : `${alert.days_remaining}d remaining`
                      }
                    </div>
                  )}
                </div>
                <div style={s.alertTitle}>{alert.title}</div>
                <div style={s.alertDesc}>{alert.description}</div>
                {alert.component_desc && (
                  <div style={s.alertComponent}>Component: {alert.component_desc}</div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      <footer style={s.pageFooter}>
        © 2026 JMB Engineering Pty Ltd — Plant Safety File Platform
      </footer>
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  container: { minHeight: '100vh', background: '#f5f6fa', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', display: 'flex', flexDirection: 'column' },
  pageFooter: { marginTop: 'auto', padding: '16px 32px', background: '#1a1a2e', color: 'rgba(255,255,255,0.4)', fontSize: '12px', textAlign: 'center' as const, letterSpacing: '0.5px' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 32px', background: '#1a1a2e', color: '#fff' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  headerTitle: { margin: 0, fontSize: '20px', fontWeight: 600 },
  pageBadge: { padding: '2px 10px', borderRadius: '12px', background: '#f44336', color: '#fff', fontSize: '11px', fontWeight: 600 },
  headerRight: { display: 'flex', alignItems: 'center', gap: '16px' },
  backBtn: { padding: '6px 12px', background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
  userName: { fontWeight: 600, fontSize: '14px' },

  summaryRow: { display: 'flex', gap: '20px', padding: '24px 32px' },
  summaryCard: { flex: 1, background: '#fff', borderRadius: '10px', padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', cursor: 'pointer', transition: 'box-shadow 0.15s' },
  summaryLabel: { fontSize: '13px', color: '#666', marginTop: '4px', fontWeight: 600 },

  filters: { display: 'flex', gap: '12px', padding: '0 32px 16px', alignItems: 'center' },
  select: { padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', background: '#fff' },
  clearBtn: { padding: '8px 16px', background: '#e0e0e0', color: '#333', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },

  alertList: { padding: '0 32px 32px', display: 'flex', flexDirection: 'column' as const, gap: '12px' },
  alertCard: { background: '#fff', borderRadius: '10px', padding: '16px 20px', borderLeft: '4px solid #999', cursor: 'pointer', transition: 'box-shadow 0.15s', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  alertTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' },
  alertMeta: { display: 'flex', gap: '8px', alignItems: 'center' },
  sevBadge: { padding: '2px 10px', borderRadius: '10px', color: '#fff', fontSize: '11px', fontWeight: 600 },
  catBadge: { fontSize: '12px', color: '#666', fontWeight: 500 },
  daysChip: { padding: '3px 12px', borderRadius: '12px', color: '#fff', fontSize: '12px', fontWeight: 700 },
  alertTitle: { fontSize: '15px', fontWeight: 600, color: '#1a1a2e', marginBottom: '4px' },
  alertDesc: { fontSize: '13px', color: '#555', lineHeight: '1.5' },
  alertComponent: { fontSize: '12px', color: '#888', marginTop: '6px', fontStyle: 'italic' as const },

  loadingText: { padding: '48px', textAlign: 'center' as const, color: '#999' },
  emptyState: { padding: '64px', textAlign: 'center' as const },
};

export default Alerts;
